import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'
import { AlertModal, AlertType } from '@/components/AlertModal'
import { Package, ArrowLeft } from 'lucide-react'
import { useLocation, useNavigate, Link, useParams } from 'react-router-dom'
import { ProductForm } from '@/components/ProductForm/ProductForm'
import { Skeleton } from '@/components/SkeletonLoader'

export default function EditProductPage() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const { productId } = useParams<{ productId: string }>()

  // Mobile detection
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Get list of feeds for feed selection
  const { data: feedsData } = useQuery({
    queryKey: ['inventory-feeds', vendorId],
    queryFn: () => api.inventory.listFeeds({ vendorId: vendorId?.toString() }),
    enabled: !!vendorId,
  })

  const feeds = feedsData?.data?.data?.items || feedsData?.data?.items || []

  // Get product from navigation state as initial data (may be incomplete)
  const productFromState = (location.state as { product?: any })?.product

  // Fetch full product details with media and offers
  const { data: productData, isLoading: isLoadingProduct, error: productError } = useQuery({
    queryKey: ['inventory-product', productId],
    queryFn: () => api.inventory.getProduct(productId!),
    enabled: !!productId,
  })

  const product = productData?.data?.data || productFromState

  // Get feed ID from product
  const [selectedFeedId, setSelectedFeedId] = useState<number | null>(null)

  // Update selectedFeedId when product loads
  useEffect(() => {
    if (product?.feed_id && !selectedFeedId) {
      setSelectedFeedId(product.feed_id)
    }
  }, [product?.feed_id, selectedFeedId])

  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean
    type: AlertType
    title: string
    message: string
    onConfirm?: () => void
    confirmText?: string
    cancelText?: string
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  })

  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: async ({ productData }: { productData: any; targetFeedId?: number }) => {
      const productIdToMatch = productId || product?.product_id
      if (!productIdToMatch) {
        throw new Error('Unable to determine product ID for this experience.')
      }

      return api.inventory.updateProduct(String(productIdToMatch), {
        ...productData,
        product_id_external:
          productData.external_product_id?.trim() ||
          product?.product_id_external ||
          null,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-feeds', vendorId] })
      queryClient.invalidateQueries({ queryKey: ['inventory-feed'] })
      queryClient.invalidateQueries({ queryKey: ['inventory-product', productId] })
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] })
      setAlertModal({
        isOpen: true,
        type: 'success',
        title: 'Success',
        message: 'Experience updated successfully!',
        onConfirm: () => {
          navigate('/vendor/inventory/experiences')
        },
        confirmText: 'OK',
      })
    },
    onError: (error: any) => {
      setAlertModal({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: error?.response?.data?.message || error?.message || 'Failed to update experience. Please try again.',
        confirmText: 'OK',
      })
    },
  })

  const handleSubmit = (productData: any, targetFeedId?: number) => {
    updateProductMutation.mutate({ productData, targetFeedId })
  }

  const handleCancel = () => {
    navigate('/vendor/inventory/experiences')
  }

  // Show loading state
  if (isLoadingProduct) {
    return (
      <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '2rem' }}>
          <Skeleton height="1rem" width="150px" style={{ marginBottom: '1rem', borderRadius: '4px' }} />
          <Skeleton height="2rem" width="300px" style={{ borderRadius: '4px' }} />
        </div>
        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '2rem', border: '1px solid var(--color-gray-200)' }}>
          <Skeleton height="3rem" style={{ marginBottom: '1.5rem', borderRadius: '4px' }} />
          <Skeleton height="8rem" style={{ marginBottom: '1.5rem', borderRadius: '4px' }} />
          <Skeleton height="3rem" style={{ marginBottom: '1.5rem', borderRadius: '4px' }} />
          <Skeleton height="3rem" style={{ borderRadius: '4px' }} />
        </div>
      </div>
    )
  }

  // Show error if product not found
  if (productError || (!isLoadingProduct && !product)) {
    return (
      <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <Package size={48} style={{ margin: '0 auto 1rem', color: 'var(--color-gray-400)' }} />
          <p style={{ fontSize: '1rem', color: 'var(--color-gray-600)', marginBottom: '1rem' }}>
            Experience not found
          </p>
          <Link
            to="/vendor/inventory/experiences"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'var(--color-sky-blue)',
              fontSize: '0.875rem',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            <ArrowLeft size={16} />
            Back to Experiences
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <Link
          to="/vendor/inventory/experiences"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
            color: 'var(--color-sky-blue)',
            fontSize: '0.875rem',
            fontWeight: 500,
            textDecoration: 'none',
            transition: 'color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--color-tixxly-navy, #0284c7)'
            e.currentTarget.style.textDecoration = 'underline'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--color-sky-blue)'
            e.currentTarget.style.textDecoration = 'none'
          }}
        >
          <ArrowLeft size={16} />
          Back to Experiences
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            backgroundColor: 'var(--color-sky-blue)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}>
            <Package size={24} />
          </div>
          <div>
            <h1 style={{
              margin: 0,
              fontSize: isMobile ? '1.5rem' : '2rem',
              fontWeight: 600,
              color: 'var(--color-gray-900)',
            }}>
              Edit Experience
            </h1>
            <p style={{
              margin: '0.25rem 0 0 0',
              fontSize: '0.875rem',
              color: 'var(--color-gray-600)',
            }}>
              {product.content?.title || product.product_id_external || product.product_id || 'Update experience details'}
            </p>
          </div>
        </div>
      </div>

      {/* Product Form */}
      <ProductForm
        product={product}
        feeds={feeds}
        selectedFeedId={selectedFeedId}
        onFeedChange={setSelectedFeedId}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={updateProductMutation.isPending}
        isMobile={isMobile}
        asModal={false}
        availableProducts={[]}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onConfirm={alertModal.onConfirm}
        onClose={() => setAlertModal({
          isOpen: false,
          type: 'info',
          title: '',
          message: ''
        })}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
      />
    </div>
  )
}
